import axios, { AxiosError } from "axios";
import { Option } from "../common/api/Model";
import { ApiResult, ErrorResponseData, getDefaultHeaders } from "../common/api/Api";
import { DEFAULT_TIMEOUT, getAppEnv } from "../common/settings";

const appEnv = getAppEnv();

export interface License {
  licenseNumber: string;
  optionId: number;
  serialId: number;
  licenseUid: number;
  option: Option;
  licenseAt: Date;
}

export interface Serial {
  id: number;
  tenantId: string;
  serialNumber: string;
  serialUid?: number;
  licenseNumber?: string;
  licenses: License[];
}

export interface SerialData {
  serials: Serial[];
}

type SerialDataResult = ApiResult<SerialData, ErrorResponseData>;

export const getListForUser = async (): Promise<SerialDataResult> => {
  try {
    const url = `${appEnv.webApiEndPoint}/app/api/serial/getListForUser`;
    const headers = { headers: getDefaultHeaders(), timeout: DEFAULT_TIMEOUT };
    const response = await axios.get<SerialData>(url, headers);

    const data = response.data;
    return { data: data };
  } catch (e) {
    const err = e as AxiosError<ErrorResponseData>;
    return { error: err };
  }
};

type SerialResult = ApiResult<Serial, ErrorResponseData>;

export const addSerial = async (serialNumber: string): Promise<SerialResult> => {
  try {
    const url = `${appEnv.webApiEndPoint}/app/api/serial/add`;
    const requestBody = { serialNumber: serialNumber };
    const headers = { headers: getDefaultHeaders(), timeout: DEFAULT_TIMEOUT };
    const response = await axios.post<Serial>(url, requestBody, headers);
    return { data: response.data };
  } catch (e) {
    const err = e as AxiosError<ErrorResponseData>;
    return { error: err };
  }
};

type LicenseResult = ApiResult<License, ErrorResponseData>;

export const searchLicense = async (licenseNumber: string): Promise<LicenseResult> => {
  try {
    const url = `${appEnv.webApiEndPoint}/app/api/license/search`;
    const requestBody = { licenseNumber: licenseNumber };
    const headers = { headers: getDefaultHeaders(), timeout: DEFAULT_TIMEOUT };
    const response = await axios.post<License>(url, requestBody, headers);

    const data = response.data;
    return { data: data };
  } catch (e) {
    const err = e as AxiosError<ErrorResponseData>;
    return { error: err };
  }
};

export const addLicense = async (serial: Serial, licenseNumber: string): Promise<SerialResult> => {
  try {
    const url = `${appEnv.webApiEndPoint}/app/api/serial/addLicense`;
    const requestBody = {
      id: serial.id,
      tenantId: serial.tenantId,
      serialNumber: serial.serialNumber,
      licenseNumber: licenseNumber,
    };
    const headers = { headers: getDefaultHeaders(), timeout: DEFAULT_TIMEOUT };
    const response = await axios.post<Serial>(url, requestBody, headers);
    return { data: response.data };
  } catch (e) {
    const err = e as AxiosError<ErrorResponseData>;
    return { error: err };
  }
};

export interface BlobData {
  data: Blob;
  fileName: string;
}

type BlobDataResult = ApiResult<BlobData, ErrorResponseData>;

export const downloadActivateFile = async (serial: Serial): Promise<BlobDataResult> => {
  try {
    const url = `${appEnv.webApiEndPoint}/app/api/serial/downloadActivateFile`;
    const requestBody = {
      id: serial.id,
      serialNumber: serial.serialNumber,
    };
    const config = {
      headers: getDefaultHeaders(),
      timeout: DEFAULT_TIMEOUT,
      responseType: "blob" as const,
    };
    const response = await axios.post<Blob>(url, requestBody, config);
    const fileName = response.headers["sc20-file-name"];
    return { data: { data: response.data, fileName: fileName } };
  } catch (e) {
    const err = e as AxiosError<ErrorResponseData>;
    return { error: err };
  }
};
